'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog/dialog';
import { Button } from '@/components/ui/button';
import { useTenantSlug } from '@/hooks/use-tenant-slug';
import { getTenantConfig } from '@/tenants/config';

const QR_SIZE = 280;
/** Logo occupies ~18% of QR width — well within the 30% error correction budget */
const LOGO_SIZE = 50;

interface QrCodeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title: string;
}

export function QrCodeModal({ open, onOpenChange, url, title }: QrCodeModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  // Per-tenant favicon for the QR centre logo (DGR helmet, DGD logo, UWR mark, etc.).
  // Falls back to the DGR favicon for any tenant without its own asset.
  const tenantSlug = useTenantSlug();
  const tenantConfig = getTenantConfig(tenantSlug);
  const logoSrc = `/images/${tenantConfig.slug}/favicon/android-chrome-192x192.png`;
  const themeLightAttr = tenantConfig.donationTheme === 'light' ? 'true' : undefined;

  const generateQr = useCallback(async () => {
    if (!canvasRef.current || !url) return;

    // Generate QR with high error correction so the center logo doesn't break scanning
    await QRCode.toCanvas(canvasRef.current, url, {
      width: QR_SIZE,
      margin: 2,
      errorCorrectionLevel: 'H',
      color: { dark: '#1c1917', light: '#ffffff' },
    });

    // Draw logo in the center
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.onload = () => {
      const x = (QR_SIZE - LOGO_SIZE) / 2;
      const y = (QR_SIZE - LOGO_SIZE) / 2;
      const padding = 4;
      const radius = 8;

      // White rounded-rect background behind logo
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(
        x - padding,
        y - padding,
        LOGO_SIZE + padding * 2,
        LOGO_SIZE + padding * 2,
        radius,
      );
      ctx.fill();

      // Clip to rounded rect and draw logo
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(x, y, LOGO_SIZE, LOGO_SIZE, radius - 2);
      ctx.clip();
      ctx.drawImage(logo, x, y, LOGO_SIZE, LOGO_SIZE);
      ctx.restore();

      setDataUrl(canvasRef.current!.toDataURL('image/png'));
    };
    logo.onerror = () => {
      // If logo fails to load, still show QR without it
      setDataUrl(canvasRef.current!.toDataURL('image/png'));
    };
    logo.src = logoSrc;
  }, [url, logoSrc]);

  useEffect(() => {
    if (open) {
      // Small delay so canvas is mounted
      const id = requestAnimationFrame(() => void generateQr());
      return () => cancelAnimationFrame(id);
    }
  }, [open, generateQr]);

  const handleDownload = () => {
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.download = `${title.replace(/\s+/g, '-').toLowerCase()}-qr.png`;
    link.href = dataUrl;
    link.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-theme-light={themeLightAttr}
        className="max-w-sm border-stone-700 bg-stone-900 text-white"
      >
        <DialogHeader>
          <DialogTitle className="text-center">QR Code</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          <div className="bg-white rounded-xl px-4 pt-3 pb-4">
            <p className="mb-2 text-center text-sm font-semibold uppercase tracking-[0.12em] text-stone-900">
              {title}
            </p>
            <canvas ref={canvasRef} />
          </div>
          <p className="text-stone-400 text-sm text-center break-all">{url}</p>
          <Button onClick={handleDownload} disabled={!dataUrl} className="w-full">
            <Download className="w-4 h-4 mr-2" />
            Download QR Code
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
